import React from 'react';
import { Card, Row, Col, Alert, List, Progress, Statistic, Tag, Space } from 'antd';
import { 
  TrophyOutlined, 
  ExclamationCircleOutlined, 
  CheckCircleOutlined, 
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';

const AnalyticsInsights = ({ analyticsData }) => {
  if (!analyticsData) return null;

  const { overview, riskAnalysis, volunteerData, trendsData } = analyticsData;

  // Generate insights based on data
  const generateInsights = () => {
    const insights = [];
    
    // Risk level insights
    const highRiskPercentage = riskAnalysis?.distribution?.find(d => 
      d.name.toLowerCase().includes('high')
    )?.percentage || 0;
    
    if (highRiskPercentage > 15) {
      insights.push({
        type: 'warning',
        title: 'High Risk Alert',
        description: `${highRiskPercentage.toFixed(1)}% of patients are classified as high risk. Consider increasing follow-up protocols.`,
        action: 'Review high-risk patients and ensure proper follow-up care.',
        priority: 'high'
      });
    }

    // Volunteer performance insights
    const avgRating = volunteerData?.performance?.avgRating || 0;
    if (avgRating < 4.5) {
      insights.push({
        type: 'info',
        title: 'Volunteer Training Opportunity',
        description: `Average volunteer rating is ${avgRating.toFixed(1)}. Consider additional training sessions.`,
        action: 'Schedule volunteer training and feedback sessions.',
        priority: 'medium'
      });
    }

    // Growth trends
    const growth = trendsData?.growth || 0;
    if (growth > 10) {
      insights.push({
        type: 'success',
        title: 'Positive Growth Trend',
        description: `Screening volume has increased by ${growth.toFixed(1)}% this period.`,
        action: 'Consider expanding capacity to meet growing demand.',
        priority: 'low'
      });
    } else if (growth < -5) {
      insights.push({
        type: 'warning',
        title: 'Declining Participation',
        description: `Screening volume has decreased by ${Math.abs(growth).toFixed(1)}% this period.`,
        action: 'Investigate causes and implement retention strategies.',
        priority: 'high'
      });
    }

    // Screening efficiency
    const totalScreenings = overview?.totalScreenings || 0;
    const activeVolunteers = volunteerData?.active || 1;
    const screeningsPerVolunteer = totalScreenings / activeVolunteers;
    
    if (screeningsPerVolunteer > 50) {
      insights.push({
        type: 'warning',
        title: 'Volunteer Workload Concern',
        description: `Average of ${screeningsPerVolunteer.toFixed(1)} screenings per volunteer may indicate overwork.`,
        action: 'Consider recruiting additional volunteers or redistributing workload.',
        priority: 'medium'
      });
    }

    return insights;
  };

  const insights = generateInsights();

  // Key performance indicators
  const kpis = [
    {
      title: 'Screening Efficiency',
      value: ((overview?.totalScreenings || 0) / Math.max(overview?.totalPatients || 1, 1) * 100),
      suffix: '%',
      status: 'success'
    },
    {
      title: 'High Risk Detection Rate',
      value: ((riskAnalysis?.highRisk || 0) / Math.max(overview?.totalPatients || 1, 1) * 100),
      suffix: '%',
      status: 'warning'
    },
    {
      title: 'Volunteer Retention',
      value: ((volunteerData?.active || 0) / Math.max(volunteerData?.total || 1, 1) * 100),
      suffix: '%',
      status: 'info'
    },
    {
      title: 'Follow-up Completion',
      value: 78, // Mock data
      suffix: '%',
      status: 'success'
    }
  ];

  // Top recommendations
  const recommendations = [
    {
      priority: 'High',
      title: 'Implement Risk Stratification Protocol',
      description: 'Develop systematic approach for high-risk patient follow-up',
      impact: 'Patient Safety',
      timeline: '2-4 weeks'
    },
    {
      priority: 'Medium',
      title: 'Volunteer Training Enhancement',
      description: 'Quarterly training sessions to improve screening quality',
      impact: 'Quality Improvement',
      timeline: '1-2 months'
    },
    {
      priority: 'Low',
      title: 'Digital Health Integration',
      description: 'Explore digital tools for patient engagement and follow-up',
      impact: 'Efficiency',
      timeline: '3-6 months'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#1890ff';
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning': return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'info': return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default: return <InfoCircleOutlined />;
    }
  };

  return (
    <div>
      {/* Key Performance Indicators */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Key Performance Indicators" size="small">
            <Row gutter={16}>
              {kpis.map((kpi, index) => (
                <Col xs={12} lg={6} key={index}>
                  <Statistic
                    title={kpi.title}
                    value={kpi.value}
                    precision={1}
                    suffix={kpi.suffix}
                    prefix={
                      kpi.value > 80 ? 
                        <ArrowUpOutlined style={{ color: '#3f8600' }} /> : 
                        kpi.value < 50 ? 
                          <ArrowDownOutlined style={{ color: '#cf1322' }} /> : 
                          null
                    }
                    valueStyle={{ 
                      color: kpi.value > 80 ? '#3f8600' : kpi.value < 50 ? '#cf1322' : '#1890ff'
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Insights and Alerts */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card title="Analytics Insights" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {insights.length > 0 ? (
                insights.map((insight, index) => (
                  <Alert
                    key={index}
                    message={
                      <Space>
                        {getInsightIcon(insight.type)}
                        <strong>{insight.title}</strong>
                        <Tag color={getPriorityColor(insight.priority)}>
                          {insight.priority.toUpperCase()}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <p style={{ margin: '8px 0' }}>{insight.description}</p>
                        <p style={{ margin: '4px 0', fontStyle: 'italic', color: '#666' }}>
                          <strong>Recommended Action:</strong> {insight.action}
                        </p>
                      </div>
                    }
                    type={insight.type}
                    showIcon={false}
                    style={{ marginBottom: 8 }}
                  />
                ))
              ) : (
                <Alert
                  message="No Critical Insights"
                  description="All metrics are within normal ranges. Continue monitoring for trends."
                  type="success"
                  showIcon
                />
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="System Health" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <div style={{ marginBottom: 8 }}>Data Quality Score</div>
                <Progress 
                  percent={92} 
                  status="active" 
                  strokeColor="#52c41a"
                  format={percent => `${percent}%`}
                />
              </div>
              <div>
                <div style={{ marginBottom: 8 }}>System Performance</div>
                <Progress 
                  percent={87} 
                  status="active" 
                  strokeColor="#1890ff"
                  format={percent => `${percent}%`}
                />
              </div>
              <div>
                <div style={{ marginBottom: 8 }}>User Satisfaction</div>
                <Progress 
                  percent={94} 
                  status="active" 
                  strokeColor="#722ed1"
                  format={percent => `${percent}%`}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Strategic Recommendations */}
      <Row gutter={24}>
        <Col span={24}>
          <Card title="Strategic Recommendations" size="small">
            <List
              dataSource={recommendations}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<TrophyOutlined style={{ color: getPriorityColor(item.priority) }} />}
                    title={
                      <Space>
                        {item.title}
                        <Tag color={getPriorityColor(item.priority)}>
                          {item.priority} Priority
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <p>{item.description}</p>
                        <Space>
                          <Tag color="blue">Impact: {item.impact}</Tag>
                          <Tag color="green">Timeline: {item.timeline}</Tag>
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsInsights;
